import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, Play, Loader2, RefreshCw } from 'lucide-react';
import { listUnits } from 'features/evals3/api/evals3Api';
import ResultPath from '../components/ResultPath';
import { displayName } from './graphModel';

/**
 * Test the eval on a real trace, without writing anything.
 *
 * Left: pick a recent run / span / conversation (searchable). Right: what
 * happened — every step in order, the edge it took, and the verdict. A judge
 * step here really calls the provider and really costs provider money; it
 * never costs an eval unit and never shows up in results.
 */

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60); if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60); if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function short(s, n = 90) {
  const t = String(s || '').replace(/^"|"$/g, '').replace(/\s+/g, ' ').trim();
  return t.length > n ? `${t.slice(0, n)}…` : t;
}

export default function TestPanel({ teamId, target, agentName, graph, unit, onUnit, result, running, onRun, onClose }) {
  const [units, setUnits] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      listUnits(teamId, { target, agentName, q, limit: 25 })
        .then((rows) => { if (!cancelled) { setUnits(rows); setError(null); } })
        .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, q ? 250 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [teamId, target, agentName, q]);

  const steps = result?.steps || [];
  const verdictLabel = useMemo(() => {
    if (!result) return null;
    if (result.state === 'resolved' && result.verdict) return result.verdict.passed ? 'PASS' : `FAIL${result.verdict.reason_code ? ` · ${result.verdict.reason_code}` : ''}`;
    return { pending: 'Waiting for a person', errored: 'Errored', not_ready: 'Sandbox not ready, try again', skipped: 'Not scored: a span variable matched nothing', stopped: 'Stopped', unit_missing: 'That trace no longer exists' }[result.state] || result.state;
  }, [result]);

  return (
    <div className="flex h-[300px] shrink-0 border-t border-[color:var(--border-default)] bg-[color:var(--surface-base)]" style={{ fontFamily: 'Inter, sans-serif' }} aria-label="Test on a trace">
      <div className="flex w-[360px] shrink-0 flex-col border-r border-[color:var(--border-default)]">
        <div className="flex shrink-0 items-center gap-1.5 border-b border-[color:var(--border-subtle)] px-2 py-1.5">
          <Search size={12} className="text-[color:var(--text-muted)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search recent ${target}s… (or paste an id)`} aria-label="Search traces"
            className="h-6 min-w-0 flex-1 bg-transparent text-[12px] text-[color:var(--text-primary)] outline-none" />
          {loading ? <Loader2 size={12} className="animate-spin text-[color:var(--text-muted)]" /> : <button type="button" onClick={() => setQ((s) => `${s}`)} aria-label="Refresh" className="text-[color:var(--text-muted)]"><RefreshCw size={12} /></button>}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {error && <div className="p-2 text-[11px] text-danger">{error}</div>}
          {!loading && !error && units.length === 0 && <div className="p-3 text-[11.5px] text-[color:var(--text-muted)]">No {target}s in the last 30 days{agentName ? ` for ${agentName}` : ''}.</div>}
          {units.map((u) => {
            const on = unit === u.id;
            return (
              <button key={u.id} type="button" onClick={() => onUnit(u.id)} aria-pressed={on}
                className={`block w-full border-b border-[color:var(--border-subtle)] px-2.5 py-1.5 text-left ${on ? 'bg-surface-hover' : 'hover:bg-surface-hover'}`}>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="font-medium text-[color:var(--text-primary)]">{u.agent_name || u.name || 'unknown'}</span>
                  {u.kind && <span className="rounded bg-surface-hover px-1 text-[10px] text-[color:var(--text-secondary)]">{u.kind}{u.tool_name ? ` · ${u.tool_name}` : ''}</span>}
                  {u.status && <span className={`text-[10px] ${u.status === 'error' ? 'text-danger' : 'text-[color:var(--text-muted)]'}`}>{u.status}</span>}
                  {u.runs_count != null && <span className="text-[10px] text-[color:var(--text-muted)]">{u.runs_count} runs</span>}
                  <span className="ml-auto text-[10px] text-[color:var(--text-muted)]">{when(u.started_at)}</span>
                </div>
                {(u.input || u.output) && (
                  <div className="mt-0.5 truncate text-[11px] text-[color:var(--text-muted)]">{short(u.input || u.output)}</div>
                )}
                <div className="truncate font-mono text-[9.5px] text-[color:var(--text-disabled)]">{u.id}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center gap-2 border-b border-[color:var(--border-subtle)] px-3 py-1.5">
          <button type="button" onClick={() => onRun()} disabled={!unit || running}
            className="inline-flex h-6 items-center gap-1 rounded-md bg-[color:var(--trodo-primary)] px-2.5 text-[11.5px] font-medium text-white hover:opacity-90 disabled:opacity-50">
            {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Run the whole eval
          </button>
          {!unit && <span className="text-[11px] text-[color:var(--text-muted)]">Pick a {target} on the left.</span>}
          {result && verdictLabel && (
            <span className={`ml-2 rounded px-2 py-0.5 text-[11px] font-semibold ${result.state === 'resolved' ? (result.verdict?.passed ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger') : 'bg-surface-hover text-[color:var(--text-secondary)]'}`}>
              {verdictLabel}{result.verdict?.score != null ? ` · ${Number(result.verdict.score).toFixed(2)}` : ''}
            </span>
          )}
          {result?.applies === false && <span className="text-[11px] text-warning">This eval's filter would skip this trace in production.</span>}
          {result?.units != null && <span className="text-[11px] text-[color:var(--text-muted)]">{result.units} unit{result.units === 1 ? '' : 's'}{result.provider_cost_usd ? ` · $${result.provider_cost_usd.toFixed(4)}` : ''}</span>}
          <div className="flex-1" />
          <button type="button" onClick={onClose} aria-label="Close test panel" className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover"><X size={14} /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
          {!result && !running && <div className="text-[12px] text-[color:var(--text-muted)]">Pick a trace and run. Every step shows what it returned and where the walk went.</div>}
          {running && <div className="flex items-center gap-2 text-[12px] text-[color:var(--text-muted)]"><Loader2 size={13} className="animate-spin" /> Running…</div>}
          {result?.error && !steps.length && <div className="text-[12px] text-danger">{result.error}</div>}
          {result?.trace && (
            <div className="mb-2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-sunken)] p-2 text-[11px] text-[color:var(--text-secondary)]">
              <span className="font-medium text-[color:var(--text-primary)]">{result.trace.agent_name || target}</span>
              {result.trace.run && <> · {result.trace.run.status} · <span className="text-[color:var(--text-muted)]">{short(result.trace.run.input, 120)}</span></>}
              {result.trace.span && <> · {result.trace.span.kind}{result.trace.span.name ? ` ${result.trace.span.name}` : ''}</>}
              {result.trace.conversation && <> · {result.trace.conversation.runs_count} runs</>}
            </div>
          )}
          {(steps.length > 0 || result?.state === 'resolved') && (
            /* The same path the results drawer draws, so a test looks like the result it would have been. */
            <ResultPath graph={graph} steps={steps}
              run={{ state: result.state, passed: result.verdict?.passed, reason_code: result.verdict?.reason_code, state_reason: result.state === 'errored' ? result.error : null, path: result.path || [] }}
              queue={null} />
          )}
        </div>
      </div>
    </div>
  );
}
